import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
interface CaseStudyCardProps {
  logo: string;
  logoAlt: string;
  logoSize?: string;
  stats: {
    value: string;
    label: string;
  }[];
  description: string;
  image: string;
  imageAlt: string;
  reversed?: boolean;
  delay?: number;
}
function CaseStudyCard({
  logo,
  logoAlt,
  logoSize = 'h-16',
  stats,
  description,
  image,
  imageAlt,
  reversed = false,
  delay = 0
}: CaseStudyCardProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const contentSection = <motion.div initial={{
    opacity: 0,
    x: reversed ? 30 : -30
  }} animate={isInView ? {
    opacity: 1,
    x: 0
  } : {
    opacity: 0,
    x: reversed ? 30 : -30
  }} transition={{
    duration: 0.6,
    delay
  }} className="flex flex-col justify-center p-10 lg:p-14 bg-white" data-id="element-3">
      <img src={logo} alt={logoAlt} className={`${logoSize} w-auto object-contain object-left mb-6`} data-id="element-4" />

      <div className="flex gap-12 mb-6" data-id="element-5">
        {stats.map((stat, index) => <div key={index} data-id="element-6">
            <p className="text-4xl lg:text-5xl font-bold text-[#0A1628] font-playfair" data-id="element-7">
              {stat.value}
            </p>
            <p className="text-xs uppercase tracking-wider text-gray-600 font-semibold mt-1" data-id="element-8">
              {stat.label}
            </p>
          </div>)}
      </div>

      <p className="text-gray-600 leading-relaxed text-base" data-id="element-9">{description}</p>
    </motion.div>;
  const imageSection = <motion.div initial={{
    opacity: 0,
    x: reversed ? -30 : 30
  }} animate={isInView ? {
    opacity: 1,
    x: 0
  } : {
    opacity: 0,
    x: reversed ? -30 : 30
  }} transition={{
    duration: 0.6,
    delay: delay + 0.1
  }} className="relative aspect-[4/3] lg:aspect-auto lg:min-h-[500px]" data-id="element-10">
      <img src={image} alt={imageAlt} className="absolute inset-0 w-full h-full object-cover" data-id="element-11" />
    </motion.div>;
  return <div ref={ref} className="bg-white rounded-xl shadow-2xl overflow-hidden grid grid-cols-1 lg:grid-cols-2" data-id="element-12">
      {reversed ? <>
          {imageSection}
          {contentSection}
        </> : <>
          {contentSection}
          {imageSection}
        </>}
    </div>;
}
export function CaseStudiesSection() {
  const caseStudies = [{
    logo: 'https://cdn.magicpatterns.com/uploads/oLVmhaHmchGmm68hi6uKhc/boats-logo-768x445.png.webp',
    logoAlt: 'Boats by George',
    logoSize: 'h-20',
    stats: [{
      value: '179%',
      label: 'Increase in\nOrganic Traffic'
    }, {
      value: '5X',
      label: 'Increase in\nOrganic Leads'
    }],
    description: "DealersChoice delivered a custom website that eliminated operational headaches cookie-cutter solutions couldn't solve.",
    image: 'https://cdn.magicpatterns.com/uploads/csLhMhW86cypD6Junytmcu/boats-by-george-show-room-2.png',
    imageAlt: 'Boats by George dealership showroom',
    reversed: false
  }, {
    logo: 'https://cdn.magicpatterns.com/uploads/nqZK5DqfRVWma3ok8abyjS/blue-water-e1756495120159.png.webp',
    logoAlt: 'Blue Water Marine',
    logoSize: 'h-16',
    stats: [{
      value: '50%',
      label: 'More Page\nViews'
    }, {
      value: '69%',
      label: 'More New\nVisitors'
    }],
    description: 'Bluewater Marine went from managing three separate sites to one SEO-optimized, custom site that improved UX, boosted leads, and automated inventory.',
    image: 'https://cdn.magicpatterns.com/uploads/p3eZfCNWnAx6RMuy93rH4z/bluewater-marine-Jacksonville-lobby-1024x576.webp',
    imageAlt: 'Blue Water Marine dealership interior',
    reversed: true
  }];
  return <section className="py-24 bg-[#0A1628] relative overflow-hidden" data-id="element-13">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5" style={{
      backgroundImage: 'radial-gradient(#D4AF37 1px, transparent 1px)',
      backgroundSize: '30px 30px'
    }} data-id="element-14"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-15">
        <div className="text-center mb-16 max-w-3xl mx-auto" data-id="element-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-playfair" data-id="element-17">
            Proven Results for Boat Dealerships
          </h2>
          <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-6" data-id="element-18"></div>
          <p className="text-gray-300 text-lg leading-relaxed" data-id="element-19">
            When your marketing aligns with how boat buyers actually shop,
            consistent, measurable progress becomes possible.
          </p>
        </div>

        <div className="space-y-16" data-id="element-20">
          {caseStudies.map((study, index) => <CaseStudyCard key={index} {...study} delay={index * 0.15} data-id="element-21" />)}
        </div>
      </div>
    </section>;
}